import React from 'react';
import { ArrowRight, MapPin, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen">
      {/* Background with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_nakasero-homes/artifacts/fwidoq50_cc523dec-427b-4202-9ec3-acea8f6d1714.jpeg"
          alt="Concord Apartments Interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/85 to-slate-900/50" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="min-h-screen flex items-center">
          <div className="grid lg:grid-cols-2 gap-12 items-center w-full py-24">
            
            {/* Left Content */}
            <div className="max-w-xl">
              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-10">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                Ready to Occupy
              </div>

              {/* Fancy Main Heading */}
              <div className="relative mb-8">
                {/* Decorative line */}
                <div className="absolute -left-6 top-0 w-1 h-full bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600 rounded-full" />
                
                {/* Main Title */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-amber-400" />
                    <span className="text-amber-400/80 text-sm font-medium tracking-[0.3em] uppercase">
                      Luxury Living
                    </span>
                  </div>
                  
                  <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl text-white font-medium leading-[0.9] tracking-tight">
                    Concord
                  </h1>
                  
                  <div className="flex items-baseline gap-4">
                    <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent font-medium tracking-tight">
                      Apartments
                    </h2>
                  </div>
                  
                  {/* Nakasero Hill with fancy styling */}
                  <div className="flex items-center gap-3 pt-4">
                    <div className="h-px w-12 bg-gradient-to-r from-amber-500 to-transparent" />
                    <span className="text-white/60 text-lg tracking-[0.15em] font-light italic">
                      Nakasero Hill
                    </span>
                    <div className="h-px w-12 bg-gradient-to-l from-amber-500 to-transparent" />
                  </div>
                  
                  {/* Tagline */}
                  <p className="text-amber-400/90 text-base md:text-lg font-medium italic pt-2">
                    &ldquo;Tailored to Your Highest Standards&rdquo;
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2 text-white/60 mb-8">
                <MapPin className="w-4 h-4 text-amber-400" />
                <span className="text-base tracking-wide">Kampala, Uganda</span>
              </div>

              {/* Description */}
              <p className="text-white/50 text-lg leading-relaxed mb-10 max-w-md">
                Experience unparalleled luxury in Kampala&apos;s most prestigious address. 
                Premium finishes, breathtaking views, world-class amenities.
              </p>

              {/* Key Features */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 mb-10">
                {['Premium Finishes', 'City Views', '24/7 Security'].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-white/60">
                    <CheckCircle className="w-4 h-4 text-amber-400" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => scrollToSection('#apartments')}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 py-6 rounded-full text-base transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-0.5"
                >
                  View Apartments
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => scrollToSection('#contact')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full text-base backdrop-blur-sm"
                >
                  Contact Us
                </Button>
              </div>
            </div>

            {/* Right Content - Price Cards */}
            <div className="hidden lg:block">
              <div className="space-y-4">
                {/* Featured Unit Card */}
                <div className="relative bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 overflow-hidden">
                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 blur-2xl" />
                  
                  <p className="text-amber-400 text-sm font-semibold uppercase tracking-wider mb-2">Starting From</p>
                  <p className="font-serif text-5xl text-white font-medium mb-1">$330,000</p>
                  <p className="text-white/40 text-sm">3 Bedroom • 190 m²</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors">
                    <p className="font-serif text-3xl text-white font-medium">3</p>
                    <p className="text-white/40 text-sm mt-1">Unit Types</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors">
                    <p className="font-serif text-3xl text-white font-medium">530</p>
                    <p className="text-white/40 text-sm mt-1">Max m²</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors">
                    <p className="font-serif text-3xl text-white font-medium">4</p>
                    <p className="text-white/40 text-sm mt-1">Lifts</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors">
                    <p className="font-serif text-3xl text-white font-medium">24/7</p>
                    <p className="text-white/40 text-sm mt-1">Security</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-amber-400/60 rounded-full mt-2 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
